/* ----------------------------------------------------------------------------
 * voicePace — PURE model + helpers for Aura's per-workspace voice pace.
 *
 * Deliberately runtime-import-free (no React, no @/lib/api, no import.meta) so it
 * is unit-testable under `node --test --experimental-strip-types` with no DOM —
 * the same discipline as src/data/active-domain.ts. The HTTP read/write path
 * lives in ./voiceSettings (which imports these).
 *
 * EXTENSIBILITY: VoiceSettings is the voice-personality config the server owns.
 * Today only `pace` is wired; future traits (tone, humour, flirtatious …) slot in
 * as sibling keys here + in the API without reworking this contract.
 * -------------------------------------------------------------------------- */

/** The per-workspace Aura voice config. `pace` is the ffmpeg atempo multiplier. */
export interface VoiceSettings {
  pace: number;
  // Reserved for future personality traits (server-owned, additive):
  // tone?: number; humour?: number; flirtatious?: number;
}

/** atempo's safe single-filter range — MUST match the server clamp (AiRoutes). */
export const PACE_MIN = 0.5;
export const PACE_MAX = 2.0;
/** Calm default (~12% slower than natural) — matches the server default. */
export const PACE_DEFAULT = 0.88;
/** Slider granularity. */
export const PACE_STEP = 0.02;

/** Clamp a pace into [PACE_MIN, PACE_MAX]; a non-finite value falls to default. */
export function clampPace(v: number): number {
  if (!Number.isFinite(v)) return PACE_DEFAULT;
  if (v < PACE_MIN) return PACE_MIN;
  if (v > PACE_MAX) return PACE_MAX;
  return v;
}

/** Snap a pace to the slider step (kills float noise like 0.8800000001). */
export function roundPace(v: number): number {
  const snapped = Math.round(clampPace(v) / PACE_STEP) * PACE_STEP;
  return clampPace(Math.round(snapped * 100) / 100);
}

/** Short human band for the current pace (for the live read-out). */
export function paceLabel(pace: number): string {
  const p = clampPace(pace);
  if (p < 0.8) return 'Slower';
  if (p > 1.2) return 'Faster';
  return 'Natural';
}

/** Display string, e.g. "0.88×". */
export function paceDisplay(pace: number): string {
  return `${clampPace(pace).toFixed(2)}×`;
}
